import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ArrowRight, ArrowLeft, User, Users, Lock, Check, 
  Calendar, Phone, Mail, Eye, EyeOff, Shield,
  Loader2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { submitRegistration } from '../../services/registrationApiService';

interface RegistrationData {
  nombres: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  pais: string;
  posicion: string;
  genero: number;
  perfilPredominante: number;
  fechaNacimiento: string;
  tutorNombre: string;
  tutorTelefono: string;
  tutorEmail: string;
  email: string;
  clave: string;
  claveConfirm: string;
  acceptTerms: boolean;
}

const initialData: RegistrationData = {
  nombres: '',
  apellidoPaterno: '',
  apellidoMaterno: '',
  pais: '',
  posicion: '',
  genero: 0,
  perfilPredominante: 0,
  fechaNacimiento: '',
  tutorNombre: '',
  tutorTelefono: '',
  tutorEmail: '',
  email: '',
  clave: '',
  claveConfirm: '',
  acceptTerms: false,
};

const COUNTRIES = [
  { code: '13', name: 'Argentina', flag: '🇦🇷' },
  { code: '46', name: 'Chile', flag: '🇨🇱' },
  { code: '52', name: 'Colombia', flag: '🇨🇴' },
  { code: '73', name: 'España', flag: '🇪🇸' },
  { code: '75', name: 'USA', flag: '🇺🇸' },
  { code: '146', name: 'México', flag: '🇲🇽' },
  { code: '173', name: 'Perú', flag: '🇵🇪' },
];

const POSITIONS: Record<string, { id: string; label: { es: string; en: string }; x: string; y: string; shortName: string }> = {
  '1': { id: '1', label: { es: 'Arquero', en: 'Goalkeeper' }, x: '50%', y: '87%', shortName: 'ARQ' },
  '2': { id: '2', label: { es: 'Central', en: 'Center-back' }, x: '65%', y: '68%', shortName: 'CEN' },
  '3': { id: '3', label: { es: 'Lateral', en: 'Wing-back' }, x: '35%', y: '68%', shortName: 'LAT' },
  '4': { id: '4', label: { es: 'Volante Central', en: 'Center Midfielder' }, x: '65%', y: '45%', shortName: 'VC' },
  '5': { id: '5', label: { es: 'Volante', en: 'Attacking Midfielder' }, x: '35%', y: '45%', shortName: 'VOL' },
  '6': { id: '6', label: { es: 'Delantero', en: 'Forward' }, x: '50%', y: '22%', shortName: 'DEL' },
};

const GENDERS = [
  { id: 1, label: { es: 'Masculino', en: 'Male' }, icon: '♂' },
  { id: 2, label: { es: 'Femenino', en: 'Female' }, icon: '♀' },
];

const DOMINANT_PROFILES = [
  { id: 1, label: { es: 'Zurdo', en: 'Left foot' }, icon: '🦶' },
  { id: 2, label: { es: 'Derecho', en: 'Right foot' }, icon: '🦶' },
];

const TOTAL_STEPS = 13;
const LOGIN_URL =
  import.meta.env.VITE_SUBSCRIPTION_LOGIN_URL ||
  'https://www.suscription.btalentstar.com/index.php?r=site/login';

const WHEEL_ITEM_H = 48;
const WHEEL_VISIBLE = 5;
const WHEEL_PAD = Math.floor(WHEEL_VISIBLE / 2);
const BIRTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const BIRTH_YEAR_END = new Date().getFullYear() - 4;
const BIRTH_YEARS = Array.from({ length: 30 }, (_, i) => BIRTH_YEAR_END - i);
const MONTHS_SHORT_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MONTHS_SHORT_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_FULL_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MONTHS_FULL_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function WheelColumn({ items, selected, onSelect }: {
  items: string[];
  selected: number;
  onSelect: (i: number) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const skipRef = useRef(false);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = selected * WHEEL_ITEM_H;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = () => {
    if (skipRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!scrollRef.current) return;
      const idx = Math.round(scrollRef.current.scrollTop / WHEEL_ITEM_H);
      const clamped = Math.max(0, Math.min(items.length - 1, idx));
      skipRef.current = true;
      scrollRef.current.scrollTo({ top: clamped * WHEEL_ITEM_H, behavior: 'smooth' });
      onSelect(clamped);
      setTimeout(() => { skipRef.current = false; }, 300);
    }, 80);
  };

  const handleClick = (i: number) => {
    skipRef.current = true;
    onSelect(i);
    scrollRef.current?.scrollTo({ top: i * WHEEL_ITEM_H, behavior: 'smooth' });
    setTimeout(() => { skipRef.current = false; }, 300);
  };

  return (
    <div className="relative flex-1 overflow-hidden" style={{ height: WHEEL_VISIBLE * WHEEL_ITEM_H }}>
      <div
        className="absolute inset-x-1 z-10 pointer-events-none rounded-xl bg-brand-yellow/10 border border-brand-yellow/30"
        style={{ top: WHEEL_PAD * WHEEL_ITEM_H, height: WHEEL_ITEM_H }}
      />
      <div
        className="absolute inset-x-0 top-0 z-20 pointer-events-none"
        style={{
          height: WHEEL_PAD * WHEEL_ITEM_H,
          background: 'linear-gradient(to bottom, rgba(15,23,42,0.97), rgba(15,23,42,0.6) 60%, transparent)',
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 z-20 pointer-events-none"
        style={{
          height: WHEEL_PAD * WHEEL_ITEM_H,
          background: 'linear-gradient(to top, rgba(15,23,42,0.97), rgba(15,23,42,0.6) 60%, transparent)',
        }}
      />
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-full overflow-y-auto overscroll-contain birth-wheel"
        style={{ scrollbarWidth: 'none' } as React.CSSProperties}
      >
        <div style={{ height: WHEEL_PAD * WHEEL_ITEM_H }} />
        {items.map((label, i) => (
          <div
            key={i}
            onClick={() => handleClick(i)}
            className={`flex items-center justify-center cursor-pointer select-none transition-all duration-200 ${
              i === selected ? 'text-brand-yellow font-bold text-xl' : 'text-dark-500 text-base'
            }`}
            style={{ height: WHEEL_ITEM_H }}
          >
            {label}
          </div>
        ))}
        <div style={{ height: WHEEL_PAD * WHEEL_ITEM_H }} />
      </div>
    </div>
  );
}

function BirthDatePicker({ value, onChange, isSpanish }: {
  value: string;
  onChange: (v: string) => void;
  isSpanish: boolean;
}) {
  const monthLabels = isSpanish ? MONTHS_SHORT_ES : MONTHS_SHORT_EN;
  const monthFullLabels = isSpanish ? MONTHS_FULL_ES : MONTHS_FULL_EN;

  const parseInit = (): [number, number, number] => {
    if (value) {
      const [y, m, d] = value.split('-').map(Number);
      if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
        const yi = BIRTH_YEARS.indexOf(y);
        return [
          Math.max(0, Math.min(30, d - 1)),
          Math.max(0, Math.min(11, m - 1)),
          yi >= 0 ? yi : 12,
        ];
      }
    }
    return [0, 0, 12];
  };

  const [initDay, initMonth, initYear] = parseInit();
  const [dayIdx, setDayIdx] = useState(initDay);
  const [monthIdx, setMonthIdx] = useState(initMonth);
  const [yearIdx, setYearIdx] = useState(initYear);

  const emitDate = (d: number, m: number, y: number) => {
    onChange(`${BIRTH_YEARS[y]}-${String(m + 1).padStart(2, '0')}-${String(BIRTH_DAYS[d]).padStart(2, '0')}`);
  };

  return (
    <div className="w-full max-w-sm mx-auto">
      <style>{`.birth-wheel::-webkit-scrollbar{display:none}`}</style>

      <div className="flex px-4 mb-2">
        <span className="flex-1 text-center text-[11px] font-bold text-dark-500 uppercase tracking-widest">
          {isSpanish ? 'Día' : 'Day'}
        </span>
        <span className="flex-1 text-center text-[11px] font-bold text-dark-500 uppercase tracking-widest">
          {isSpanish ? 'Mes' : 'Month'}
        </span>
        <span className="flex-1 text-center text-[11px] font-bold text-dark-500 uppercase tracking-widest">
          {isSpanish ? 'Año' : 'Year'}
        </span>
      </div>

      <div className="flex bg-brand-navy/60 backdrop-blur-xl rounded-2xl border-2 border-brand-lightBlue/30 p-2 overflow-hidden">
        <WheelColumn
          items={BIRTH_DAYS.map(d => String(d).padStart(2, '0'))}
          selected={dayIdx}
          onSelect={(i) => { setDayIdx(i); emitDate(i, monthIdx, yearIdx); }}
        />
        <div className="w-px bg-brand-lightBlue/15 my-8 flex-shrink-0" />
        <WheelColumn
          items={monthLabels}
          selected={monthIdx}
          onSelect={(i) => { setMonthIdx(i); emitDate(dayIdx, i, yearIdx); }}
        />
        <div className="w-px bg-brand-lightBlue/15 my-8 flex-shrink-0" />
        <WheelColumn
          items={BIRTH_YEARS.map(String)}
          selected={yearIdx}
          onSelect={(i) => { setYearIdx(i); emitDate(dayIdx, monthIdx, i); }}
        />
      </div>

      <motion.div
        key={`${dayIdx}-${monthIdx}-${yearIdx}`}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center gap-2 mt-4"
      >
        <Calendar className="w-4 h-4 text-brand-yellow/60" />
        <span className="text-brand-yellow/80 text-sm font-medium">
          {BIRTH_DAYS[dayIdx]} {monthFullLabels[monthIdx]}, {BIRTH_YEARS[yearIdx]}
        </span>
      </motion.div>
    </div>
  );
}

interface DynamicInputProps {
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  icon?: React.ComponentType<{ className?: string }>;
  rightIcon?: React.ReactNode;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

const DynamicInput = React.memo(function DynamicInput({
  type = 'text',
  value,
  onChange,
  placeholder,
  icon: Icon,
  rightIcon,
  inputRef,
  onKeyDown,
}: DynamicInputProps) {
  return (
    <div className="relative w-full max-w-md mx-auto group">
      {Icon && (
        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-dark-400 group-focus-within:text-brand-yellow transition-colors z-10" />
      )}
      <input
        ref={inputRef}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={`
          w-full ${Icon ? 'pl-14' : 'pl-6'} ${rightIcon ? 'pr-14' : 'pr-6'} py-5
          text-2xl md:text-3xl font-medium text-center text-white
          bg-brand-navy/60 backdrop-blur-xl
          border-2 border-brand-lightBlue/30
          rounded-2xl
          placeholder-dark-500
          focus:outline-none focus:border-brand-yellow/60 focus:bg-brand-navy/80
          focus:ring-2 focus:ring-brand-yellow/20 focus:shadow-xl focus:shadow-brand-yellow/10
          transition-all duration-300 ease-out
          ${type === 'date' ? '[color-scheme:dark] cursor-pointer' : ''}
        `}
      />
      {rightIcon && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2 z-10">
          {rightIcon}
        </div>
      )}
      <div className="absolute inset-0 rounded-2xl bg-brand-yellow/5 opacity-0 group-focus-within:opacity-100 transition-opacity pointer-events-none" />
    </div>
  );
});

interface RegistrationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin?: () => void;
}

export function RegistrationWizard({ isOpen, onClose }: RegistrationWizardProps) {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';
  
  const [step, setStep] = useState(1);
  const [data, setData] = useState<RegistrationData>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const updateField = useCallback(<K extends keyof RegistrationData>(field: K, value: RegistrationData[K]) => {
    setData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleClose = useCallback(() => {
    setStep(1);
    setData(initialData);
    setSubmitError(null);
    onClose();
  }, [onClose]);

  const canProceed = () => {
    switch (step) {
      case 1: return data.nombres.length >= 2;
      case 2: return data.apellidoPaterno.length >= 2;
      case 3: return data.apellidoMaterno.length >= 2;
      case 4: return /\S+@\S+\.\S+/.test(data.email);
      case 5: return data.clave.length >= 6 && data.clave === data.claveConfirm;
      case 6: return !!data.pais;
      case 7: return !!data.fechaNacimiento;
      case 8: return data.genero > 0;
      case 9: return !!data.posicion;
      case 10: return data.perfilPredominante > 0;
      case 11: return data.tutorNombre.length >= 2;
      case 12: return data.tutorTelefono.length >= 8 && /\S+@\S+\.\S+/.test(data.tutorEmail);
      case 13: return data.acceptTerms;
      default: return false;
    }
  };

  const nextStep = () => {
    if (!canProceed()) {
      const el = document.activeElement as HTMLElement;
      el?.animate([
        { transform: 'translateX(-4px)' },
        { transform: 'translateX(4px)' },
        { transform: 'translateX(-4px)' },
        { transform: 'translateX(4px)' },
        { transform: 'translateX(0)' },
      ], { duration: 200, easing: 'ease-in-out' });
      return;
    }
    if (step < TOTAL_STEPS) {
      setStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await submitRegistration({ ...data, lenguaje: isSpanish ? 0 : 1 });
      setStep(TOTAL_STEPS + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : (isSpanish ? 'Error al crear la cuenta' : 'Error creating account');
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const prevStepRef = useRef(step);
  useEffect(() => {
    if (isOpen && step !== prevStepRef.current) {
      prevStepRef.current = step;
      const timer = setTimeout(() => inputRef.current?.focus(), 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen, step]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canProceed()) {
      nextStep();
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <StepWrapper>
            <StepQuestion>
              {isSpanish ? 'Me llamo' : "My name is"}
            </StepQuestion>
            <DynamicInput
              value={data.nombres}
              onChange={(v) => updateField('nombres', v)}
              placeholder={isSpanish ? 'tu nombre' : 'your name'}
              icon={User}
              inputRef={inputRef}
              onKeyDown={handleKeyDown}
            />
          </StepWrapper>
        );

      case 2:
        return (
          <StepWrapper>
            <StepQuestion>
              {isSpanish ? 'Mi apellido paterno es' : "My last name is"}
            </StepQuestion>
            <DynamicInput
              value={data.apellidoPaterno}
              onChange={(v) => updateField('apellidoPaterno', v)}
              placeholder={isSpanish ? 'apellido paterno' : 'last name'}
              icon={User}
              inputRef={inputRef}
              onKeyDown={handleKeyDown}
            />
          </StepWrapper>
        );

      case 3:
        return (
          <StepWrapper>
            <StepQuestion>
              {isSpanish ? 'Mi apellido materno es' : "My mother's last name is"}
            </StepQuestion>
            <DynamicInput
              value={data.apellidoMaterno}
              onChange={(v) => updateField('apellidoMaterno', v)}
              placeholder={isSpanish ? 'apellido materno' : "mother's last name"}
              icon={User}
              inputRef={inputRef}
              onKeyDown={handleKeyDown}
            />
          </StepWrapper>
        );

      case 4:
        return (
          <StepWrapper>
            <StepQuestion>
              {isSpanish ? 'Mi email es' : "My email is"}
            </StepQuestion>
            <DynamicInput
              type="email"
              value={data.email}
              onChange={(v) => updateField('email', v)}
              placeholder="tu@email.com"
              icon={Mail}
              inputRef={inputRef}
              onKeyDown={handleKeyDown}
            />
          </StepWrapper>
        );

      case 5:
        return (
          <StepWrapper>
            <StepQuestion>
              {isSpanish ? 'Mi contraseña será' : "My password will be"}
            </StepQuestion>
            <DynamicInput
              type={showPassword ? 'text' : 'password'}
              value={data.clave}
              onChange={(v) => updateField('clave', v)}
              placeholder="••••••••"
              icon={Lock}
              inputRef={inputRef}
              onKeyDown={handleKeyDown}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-dark-400 hover:text-white transition-colors p-2"
                >
                  {showPassword ? <EyeOff size={24} /> : <Eye size={24} />}
                </button>
              }
            />
            <div className="mt-6 w-full max-w-md mx-auto">
              <p className="text-dark-400 text-sm mb-3 text-center">
                {isSpanish ? 'Confirmar contraseña' : 'Confirm password'}
              </p>
              <DynamicInput
                type={showConfirmPassword ? 'text' : 'password'}
                value={data.claveConfirm}
                onChange={(v) => updateField('claveConfirm', v)}
                placeholder="••••••••"
                icon={Lock}
                onKeyDown={handleKeyDown}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-dark-400 hover:text-white transition-colors p-2"
                  >
                    {showConfirmPassword ? <EyeOff size={24} /> : <Eye size={24} />}
                  </button>
                }
              />
            </div>
          </StepWrapper>
        );

      case 6:
        return (
          <StepWrapper>
            <StepQuestion>
              {isSpanish ? 'Soy de' : "I'm from"}
            </StepQuestion>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto">
              {COUNTRIES.map((country) => (
                <motion.button
                  key={country.code}
                  onClick={() => updateField('pais', country.code)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`
                    p-4 rounded-xl border-2 transition-all duration-300
                    flex flex-col items-center gap-2
                    ${data.pais === country.code 
                      ? 'border-brand-yellow bg-brand-yellow/20 shadow-lg shadow-brand-yellow/20' 
                      : 'border-brand-lightBlue/30 bg-brand-navy/60 hover:border-brand-lightBlue/50'
                    }
                  `}
                >
                  <span className="text-3xl">{country.flag}</span>
                  <span className={`text-sm font-medium ${data.pais === country.code ? 'text-brand-yellow' : 'text-dark-300'}`}>
                    {country.name}
                  </span>
                </motion.button>
              ))}
            </div>
          </StepWrapper>
        );

      case 7:
        return (
          <StepWrapper>
            <StepQuestion>
              {isSpanish ? 'Nací el' : "I was born on"}
            </StepQuestion>
            <BirthDatePicker
              value={data.fechaNacimiento}
              onChange={(v) => updateField('fechaNacimiento', v)}
              isSpanish={isSpanish}
            />
          </StepWrapper>
        );

      case 8:
        return (
          <StepWrapper>
            <StepQuestion>
              {isSpanish ? '¿Cuál es tu sexo?' : 'What is your sex?'}
            </StepQuestion>
            <div className="flex gap-4 max-w-sm mx-auto w-full">
              {GENDERS.map((g) => {
                const selected = data.genero === g.id;
                return (
                  <motion.button
                    key={g.id}
                    onClick={() => updateField('genero', g.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`
                      flex-1 p-6 rounded-2xl border-2 flex flex-col items-center gap-3 transition-all duration-300
                      ${selected
                        ? 'border-brand-yellow bg-brand-yellow/15 shadow-lg shadow-brand-yellow/20'
                        : 'border-brand-lightBlue/30 bg-brand-navy/60 hover:border-brand-lightBlue/50'
                      }
                    `}
                  >
                    <span className="text-4xl">{g.icon}</span>
                    <span className={`text-lg font-bold ${selected ? 'text-brand-yellow' : 'text-dark-300'}`}>
                      {isSpanish ? g.label.es : g.label.en}
                    </span>
                    {selected && (
                      <div className="w-5 h-5 rounded-full bg-brand-yellow flex items-center justify-center">
                        <Check className="w-3 h-3 text-brand-darkNavy" />
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </StepWrapper>
        );

      case 9:
        return (
          <StepWrapper>
            <StepQuestion>
              {isSpanish ? '¿En qué posición juegas?' : "What position do you play?"}
            </StepQuestion>
            <div className="relative w-full max-w-xs mx-auto aspect-[3/4] bg-pitch-900/50 rounded-2xl overflow-hidden border-2 border-pitch-500/30 backdrop-blur-sm">
              <div className="absolute inset-0">
                <div className="absolute inset-3 border-2 border-pitch-400/40 rounded-lg" />
                <div className="absolute left-3 right-3 top-1/2 h-[2px] bg-pitch-400/30" />
                <div className="absolute left-1/2 -translate-x-1/2 top-[calc(50%-1.5rem)] w-12 h-12 rounded-full border-2 border-pitch-400/40" />
                <div className="absolute left-[15%] right-[15%] top-[3%] h-[22%] border-2 border-pitch-400/40 rounded-sm" />
                <div className="absolute left-[15%] right-[15%] bottom-[3%] h-[22%] border-2 border-pitch-400/40 rounded-sm" />
              </div>
              
              {Object.values(POSITIONS).map((pos) => {
                const selected = data.posicion === pos.id;
                return (
                  <button
                    key={pos.id}
                    onClick={() => updateField('posicion', pos.id)}
                    className="absolute -translate-x-1/2 -translate-y-1/2 group"
                    style={{ left: pos.x, top: pos.y }}
                  >
                    <div className={`
                      w-11 h-11 sm:w-13 sm:h-13 rounded-full flex items-center justify-center
                      text-xs sm:text-sm font-bold shadow-lg
                      transition-colors duration-200
                      active:scale-90
                      ${selected
                        ? 'bg-brand-yellow text-brand-darkNavy border-2 border-brand-yellow shadow-brand-yellow/50'
                        : 'bg-brand-navy/80 text-white border border-pitch-500/50 group-hover:border-pitch-400 group-hover:shadow-pitch-500/30'
                      }
                    `}>
                      {pos.shortName}
                    </div>
                    {selected && (
                      <div className="absolute inset-0 rounded-full border-2 border-brand-yellow/40 animate-ping" />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-6 text-center">
              <span className={`
                inline-block px-6 py-2 rounded-full text-lg font-bold transition-all duration-200
                ${data.posicion 
                  ? 'bg-gradient-to-r from-brand-yellow to-brand-orange text-brand-darkNavy' 
                  : 'bg-brand-navy/60 text-dark-400 border border-brand-lightBlue/30'
                }
              `}>
                {data.posicion
                  ? (isSpanish ? POSITIONS[data.posicion]?.label.es : POSITIONS[data.posicion]?.label.en)
                  : (isSpanish ? 'Selecciona una posición' : 'Select a position')
                }
              </span>
            </div>
          </StepWrapper>
        );

      case 10:
        return (
          <StepWrapper>
            <StepQuestion>
              {isSpanish ? '¿Cuál es tu perfil predominante?' : 'What is your dominant foot?'}
            </StepQuestion>
            <div className="flex gap-4 max-w-sm mx-auto w-full">
              {DOMINANT_PROFILES.map((dp) => {
                const selected = data.perfilPredominante === dp.id;
                return (
                  <motion.button
                    key={dp.id}
                    onClick={() => updateField('perfilPredominante', dp.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`
                      flex-1 p-6 rounded-2xl border-2 flex flex-col items-center gap-3 transition-all duration-300
                      ${selected
                        ? 'border-brand-yellow bg-brand-yellow/15 shadow-lg shadow-brand-yellow/20'
                        : 'border-brand-lightBlue/30 bg-brand-navy/60 hover:border-brand-lightBlue/50'
                      }
                    `}
                  >
                    <span className={`text-4xl ${dp.id === 1 ? '-scale-x-100' : ''}`}>{dp.icon}</span>
                    <span className={`text-lg font-bold ${selected ? 'text-brand-yellow' : 'text-dark-300'}`}>
                      {isSpanish ? dp.label.es : dp.label.en}
                    </span>
                    {selected && (
                      <div className="w-5 h-5 rounded-full bg-brand-yellow flex items-center justify-center">
                        <Check className="w-3 h-3 text-brand-darkNavy" />
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </StepWrapper>
        );

      case 11:
        return (
          <StepWrapper>
            <StepQuestion>
              {isSpanish ? 'Mi tutor/padre es' : "My guardian/parent is"}
            </StepQuestion>
            <DynamicInput
              value={data.tutorNombre}
              onChange={(v) => updateField('tutorNombre', v)}
              placeholder={isSpanish ? 'nombre completo' : 'full name'}
              icon={Users}
              inputRef={inputRef}
              onKeyDown={handleKeyDown}
            />
          </StepWrapper>
        );

      case 12:
        return (
          <StepWrapper>
            <StepQuestion>
              {isSpanish ? 'Contacto del tutor' : "Guardian contact"}
            </StepQuestion>
            <div className="space-y-4 w-full max-w-md mx-auto">
              <DynamicInput
                type="tel"
                value={data.tutorTelefono}
                onChange={(v) => updateField('tutorTelefono', v)}
                placeholder={isSpanish ? '+56 9 0000 0000' : '+1 555 000 0000'}
                icon={Phone}
                inputRef={inputRef}
                onKeyDown={handleKeyDown}
              />
              <DynamicInput
                type="email"
                value={data.tutorEmail}
                onChange={(v) => updateField('tutorEmail', v)}
                placeholder={isSpanish ? 'email del tutor' : 'guardian email'}
                icon={Mail}
                onKeyDown={handleKeyDown}
              />
            </div>
          </StepWrapper>
        );

      case 13:
        return (
          <StepWrapper>
            <StepQuestion>
              {isSpanish ? '¡Último paso!' : "Last step!"}
            </StepQuestion>
            <div className="max-w-md mx-auto space-y-6">
              {/* Summary */}
              <div className="bg-brand-navy/60 backdrop-blur-xl rounded-2xl p-6 border border-brand-lightBlue/30">
                <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-brand-yellow" />
                  {isSpanish ? 'Resumen' : 'Summary'}
                </h4>
                <div className="space-y-2 text-sm">
                  <p className="text-dark-300"><span className="text-white font-medium">{isSpanish ? 'Nombre:' : 'Name:'}</span> {data.nombres} {data.apellidoPaterno} {data.apellidoMaterno}</p>
                  <p className="text-dark-300"><span className="text-white font-medium">Email:</span> {data.email}</p>
                  <p className="text-dark-300"><span className="text-white font-medium">{isSpanish ? 'País:' : 'Country:'}</span> {COUNTRIES.find(c => c.code === data.pais)?.flag} {COUNTRIES.find(c => c.code === data.pais)?.name}</p>
                  <p className="text-dark-300"><span className="text-white font-medium">{isSpanish ? 'Sexo:' : 'Sex:'}</span> {(() => { const g = GENDERS.find(g => g.id === data.genero); return g ? (isSpanish ? g.label.es : g.label.en) : ''; })()}</p>
                  <p className="text-dark-300"><span className="text-white font-medium">{isSpanish ? 'Posición:' : 'Position:'}</span> {data.posicion ? (isSpanish ? POSITIONS[data.posicion]?.label.es : POSITIONS[data.posicion]?.label.en) : ''}</p>
                  <p className="text-dark-300"><span className="text-white font-medium">{isSpanish ? 'Perfil:' : 'Profile:'}</span> {(() => { const d = DOMINANT_PROFILES.find(d => d.id === data.perfilPredominante); return d ? (isSpanish ? d.label.es : d.label.en) : ''; })()}</p>
                  <p className="text-dark-300"><span className="text-white font-medium">{isSpanish ? 'Tutor:' : 'Guardian:'}</span> {data.tutorNombre}</p>
                </div>
              </div>
              
              {/* Terms checkbox */}
              <label className="flex items-start gap-4 cursor-pointer group">
                <div className="relative mt-1">
                  <input
                    type="checkbox"
                    checked={data.acceptTerms}
                    onChange={(e) => updateField('acceptTerms', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className={`
                    w-6 h-6 rounded-md border-2 transition-all duration-300
                    flex items-center justify-center
                    ${data.acceptTerms 
                      ? 'bg-brand-yellow border-brand-yellow' 
                      : 'border-brand-lightBlue/50 group-hover:border-brand-yellow/50'
                    }
                  `}>
                    {data.acceptTerms && <Check className="w-4 h-4 text-brand-darkNavy" />}
                  </div>
                </div>
                <span className="text-sm text-dark-300 leading-relaxed">
                  {isSpanish ? (
                    <>
                      Acepto el tratamiento de mis datos personales según lo descrito en las{' '}
                      <a
                        href="https://sistema.bsports.cl/descargas/PRIVACITY_ESP.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-yellow hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Política de Privacidad
                      </a>
                      {' '}y estoy de acuerdo con los{' '}
                      <a
                        href="https://sistema.bsports.cl/descargas/TOS_PAY_ESP.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-yellow hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Términos y Condiciones
                      </a>
                      {' '}de BTalentStar.
                    </>
                  ) : (
                    <>
                      I accept the processing of my personal data as described in the{' '}
                      <a
                        href="https://sistema.bsports.cl/descargas/PRIVACITY_ESP.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-yellow hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </a>
                      {' '}and agree to the{' '}
                      <a
                        href="https://sistema.bsports.cl/descargas/TOS_PAY_ESP.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-yellow hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms and Conditions
                      </a>
                      {' '}of BTalentStar.
                    </>
                  )}
                </span>
              </label>

              {submitError && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm text-center">
                  {submitError}
                </div>
              )}
            </div>
          </StepWrapper>
        );

      case TOTAL_STEPS + 1:
        return (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex flex-col items-center justify-center text-center px-4"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="w-24 h-24 rounded-full bg-gradient-to-br from-pitch-500 to-pitch-600 flex items-center justify-center mb-8 shadow-2xl shadow-pitch-500/40"
            >
              <Check className="w-12 h-12 text-white" />
            </motion.div>
            <div className="w-full max-w-lg rounded-3xl border border-pitch-500/30 bg-brand-navy/70 p-6 md:p-8 mb-8 shadow-2xl backdrop-blur-xl">
              <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                {isSpanish ? '¡Cuenta creada!' : 'Account created!'}
              </h2>
              <p className="text-lg text-dark-300">
                {isSpanish
                  ? 'Recibirás un correo electrónico en tu bandeja de entrada. Ábrelo y activa tu cuenta antes de iniciar sesión.'
                  : 'You will receive an email in your inbox. Open it and activate your account before signing in.'
                }
              </p>
              <p className="mt-3 text-sm text-dark-400">
                {isSpanish
                  ? 'Si no lo encuentras, revisa también tu carpeta de spam o correo no deseado.'
                  : 'If you cannot find it, please also check your spam or junk folder.'
                }
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.assign(LOGIN_URL)}
              className="px-8 py-4 bg-gradient-to-r from-brand-yellow to-brand-orange text-brand-darkNavy font-bold rounded-full text-lg shadow-xl shadow-brand-yellow/30 hover:shadow-brand-yellow/50 transition-shadow"
            >
              {isSpanish ? 'Ir a iniciar sesión' : 'Go to sign in'}
            </motion.button>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] isolate"
          style={{ pointerEvents: 'auto' }}
        >
          <div className="absolute inset-0 bg-brand-darkNavy" />
          <div className="absolute inset-0 bg-gradient-radial from-brand-navy/50 via-transparent to-transparent pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,215,0,0.05)_0%,transparent_50%)] pointer-events-none" />
          
          {/* Close button */}
          <motion.button
            onClick={handleClose}
            className="absolute top-4 right-4 p-3 rounded-xl hover:bg-white/10 transition-colors z-[100]"
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <X className="w-6 h-6 text-dark-400" />
          </motion.button>

          {/* Progress bar */}
          {step <= TOTAL_STEPS && (
            <div className="fixed top-0 left-0 right-0 h-1.5 bg-brand-navy/50 z-[50]">
              <motion.div
                className="h-full bg-gradient-to-r from-brand-yellow via-brand-orange to-pitch-500"
                initial={{ width: '0%' }}
                animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
                transition={{ type: 'spring', stiffness: 100, damping: 20 }}
              />
              <motion.div
                className="absolute top-0 left-0 h-full w-1/4 bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none"
                animate={{ x: ['-100%', '500%'] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              />
            </div>
          )}

          {/* Main content */}
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 pb-40 pt-16 overflow-y-auto pointer-events-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -30, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="w-full max-w-2xl"
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation buttons + login */}
          {step <= TOTAL_STEPS && (
            <div className="fixed bottom-0 left-0 right-0 z-[60] flex flex-col items-center gap-3 p-4 md:p-6 bg-gradient-to-t from-brand-darkNavy via-brand-darkNavy/95 to-transparent pointer-events-none">
              <p className="pointer-events-auto text-center text-sm text-dark-400">
                {isSpanish ? '¿Ya tienes cuenta?' : 'Already have an account?'}{' '}
                <a
                  href={LOGIN_URL}
                  className="text-brand-yellow hover:underline font-medium"
                >
                  {isSpanish ? 'Inicia sesión' : 'Sign in'}
                </a>
              </p>
              <div className="pointer-events-auto flex justify-center gap-4">
              {step > 1 && (
                <motion.button
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={prevStep}
                  className="px-6 md:px-10 py-3 md:py-4 bg-brand-navy/80 backdrop-blur-sm rounded-xl text-white font-medium text-lg flex items-center gap-2 border border-brand-lightBlue/30 hover:border-brand-lightBlue/50 transition-colors"
                >
                  <motion.span animate={{ x: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
                    <ArrowLeft className="w-5 h-5" />
                  </motion.span>
                  <span className="hidden sm:inline">{isSpanish ? 'Anterior' : 'Previous'}</span>
                </motion.button>
              )}

              <motion.button
                initial={{ opacity: 0, x: 20 }}
                animate={{ 
                  opacity: 1, 
                  x: 0,
                  backgroundColor: canProceed() ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                  borderColor: canProceed() ? 'rgba(34, 197, 94, 0.4)' : 'rgba(239, 68, 68, 0.3)',
                }}
                whileHover={canProceed() ? { scale: 1.05 } : {}}
                whileTap={canProceed() ? { scale: 0.95 } : {}}
                onClick={nextStep}
                disabled={isSubmitting}
                className={`px-6 md:px-10 py-3 md:py-4 backdrop-blur-sm rounded-xl font-bold text-lg flex items-center gap-2 border transition-all relative overflow-hidden
                  ${canProceed() 
                    ? 'text-pitch-400 hover:shadow-lg hover:shadow-pitch-500/20' 
                    : 'text-red-400 cursor-not-allowed'
                  }
                `}
              >
                {isSubmitting && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                )}
                <span>
                  {isSubmitting 
                    ? (isSpanish ? 'Creando...' : 'Creating...') 
                    : step < TOTAL_STEPS 
                      ? (isSpanish ? 'Siguiente' : 'Next')
                      : (isSpanish ? '¡Crear Cuenta!' : 'Create Account!')
                  }
                </span>
                {!isSubmitting && step < TOTAL_STEPS && (
                  <motion.span animate={{ x: [0, 4, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
                    <ArrowRight className="w-5 h-5" />
                  </motion.span>
                )}
                {isSubmitting && <Loader2 className="w-5 h-5 animate-spin" />}
              </motion.button>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StepWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-8">
      {children}
    </div>
  );
}

function StepQuestion({ children }: { children: React.ReactNode }) {
  return (
    <motion.h2
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-2xl md:text-4xl font-bold text-white text-center tracking-wide"
    >
      {children}
    </motion.h2>
  );
}

export default RegistrationWizard;
